import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { FacilityAvailability, GridTimeSlot } from '../../types/facility';
import { generateTimeSlots } from '../../types/facility';

interface ScheduleEditorProps {
  availability: FacilityAvailability;
  onChange: (availability: FacilityAvailability) => void;
  isLoadingSpecificDate?: boolean;
  onSpecificDateChange?: (date: string) => void;
}

type ScheduleMode = 'general' | 'specific';
type DayType = 'weekdays' | 'saturday' | 'sunday';

// Format date for API (YYYY-MM-DD)
function formatDateForApi(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function ScheduleEditor({
  availability,
  onChange,
  isLoadingSpecificDate = false,
  onSpecificDateChange
}: ScheduleEditorProps) {
  const { t } = useTranslation();
  const [scheduleMode, setScheduleMode] = useState<ScheduleMode>('general');
  const [selectedDay, setSelectedDay] = useState<DayType>('weekdays');
  const [specificDate, setSpecificDate] = useState<string>(formatDateForApi(new Date()));
  const [justCopied, setJustCopied] = useState(false);

  // Initialize specific date slots when switching to specific mode
  useEffect(() => {
    if (scheduleMode === 'specific' && !availability.specificDates[specificDate]) {
      onChange({
        ...availability,
        specificDates: {
          ...availability.specificDates,
          [specificDate]: generateTimeSlots(),
        },
      });
    }
  }, [scheduleMode, specificDate]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value;
    setSpecificDate(newDate);

    // Initialize slots for new date if they don't exist
    if (!availability.specificDates[newDate]) {
      onChange({
        ...availability,
        specificDates: {
          ...availability.specificDates,
          [newDate]: generateTimeSlots(),
        },
      });
    }

    // Notify parent to load from API if editing existing facility
    onSpecificDateChange?.(newDate);
  };

  const getCurrentSlots = (): GridTimeSlot[] => {
    if (scheduleMode === 'general') {
      return availability[selectedDay] || generateTimeSlots();
    } else {
      return availability.specificDates[specificDate] || generateTimeSlots();
    }
  };

  const toggleTimeSlot = (slotId: string) => {
    const currentSlots = getCurrentSlots();
    const updatedSlots = currentSlots.map(slot =>
      slot.id === slotId ? { ...slot, isAvailable: !slot.isAvailable } : slot
    );

    if (scheduleMode === 'general') {
      onChange({
        ...availability,
        [selectedDay]: updatedSlots,
      });
    } else {
      onChange({
        ...availability,
        specificDates: {
          ...availability.specificDates,
          [specificDate]: updatedSlots,
        },
      });
    }
  };

  const setAllAvailable = () => {
    const updatedSlots = getCurrentSlots().map(slot => ({
      ...slot,
      isAvailable: true,
    }));

    if (scheduleMode === 'general') {
      onChange({
        ...availability,
        [selectedDay]: updatedSlots,
      });
    } else {
      onChange({
        ...availability,
        specificDates: {
          ...availability.specificDates,
          [specificDate]: updatedSlots,
        },
      });
    }
  };

  const setAllUnavailable = () => {
    const updatedSlots = getCurrentSlots().map(slot => ({
      ...slot,
      isAvailable: false,
    }));

    if (scheduleMode === 'general') {
      onChange({
        ...availability,
        [selectedDay]: updatedSlots,
      });
    } else {
      onChange({
        ...availability,
        specificDates: {
          ...availability.specificDates,
          [specificDate]: updatedSlots,
        },
      });
    }
  };

  const copyToOtherDays = () => {
    if (scheduleMode !== 'general') return;
    const currentSlots = availability[selectedDay];
    onChange({
      ...availability,
      weekdays: selectedDay === 'weekdays' ? currentSlots : [...currentSlots],
      saturday: selectedDay === 'saturday' ? currentSlots : [...currentSlots],
      sunday: selectedDay === 'sunday' ? currentSlots : [...currentSlots],
    });
    setJustCopied(true);
    setTimeout(() => setJustCopied(false), 1500);
  };

  const getSlotStyle = (slot: GridTimeSlot): string => {
    if (slot.isBooked) {
      return 'bg-brand-warm/30 border-brand-warm text-brand-warm-deep dark:text-brand-warm cursor-not-allowed';
    }
    if (slot.isAvailable) {
      return 'bg-brand-confirm/30 border-brand-confirm text-brand-confirm-deep dark:text-brand-confirm hover:bg-brand-confirm/100/40 cursor-pointer';
    }
    return 'bg-surface-app-elevated border-border-strong text-ink-secondary hover:bg-surface-card-hover/50 cursor-pointer';
  };

  const dayLabels: Record<DayType, string> = {
    weekdays: t('facilities.schedule.weekdays', 'Dni robocze'),
    saturday: t('facilities.schedule.saturday', 'Sobota'),
    sunday: t('facilities.schedule.sunday', 'Niedziela'),
  };

  return (
    <div className="space-y-4">
      {/* Schedule Mode Toggle */}
      <div>
        <label className="block text-sm font-medium text-ink-primary mb-2">
          {t('facilities.schedule.scheduleType', 'Typ harmonogramu')}
        </label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setScheduleMode('general')}
            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              scheduleMode === 'general'
                ? 'bg-brand-primary text-white'
                : 'bg-surface-card text-ink-primary border border-border-strong hover:bg-surface-card-hover'
            }`}
          >
            {t('facilities.schedule.generalSchedule', 'Harmonogram ogólny')}
          </button>
          <button
            type="button"
            onClick={() => setScheduleMode('specific')}
            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              scheduleMode === 'specific'
                ? 'bg-brand-primary text-white'
                : 'bg-surface-card text-ink-primary border border-border-strong hover:bg-surface-card-hover'
            }`}
          >
            {t('facilities.schedule.specificDates', 'Konkretne daty')}
          </button>
        </div>
      </div>

      {/* Day Selection (General Mode) */}
      {scheduleMode === 'general' && (
        <div>
          <label className="block text-sm font-medium text-ink-primary mb-2">
            {t('facilities.schedule.selectDay', 'Wybierz dzień')}
          </label>
          <div className="flex gap-2">
            {(Object.keys(dayLabels) as DayType[]).map(day => (
              <button
                key={day}
                type="button"
                onClick={() => setSelectedDay(day)}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedDay === day
                    ? 'bg-brand-primary text-white'
                    : 'bg-surface-card text-ink-primary border border-border-strong hover:bg-surface-card-hover'
                }`}
              >
                {dayLabels[day]}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Date Picker (Specific Mode) */}
      {scheduleMode === 'specific' && (
        <div>
          <label className="block text-sm font-medium text-ink-primary mb-2">
            {t('facilities.schedule.selectDate', 'Wybierz datę')}
          </label>
          <input
            type="date"
            value={specificDate}
            onChange={handleDateChange}
            className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/50"
          />
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-brand-confirm/30 border border-brand-confirm" />
          <span className="text-ink-secondary">{t('facilities.schedule.available', 'Dostępne')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-surface-app-elevated border border-border-strong" />
          <span className="text-ink-secondary">{t('facilities.schedule.unavailable', 'Niedostępne')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-brand-warm/30 border border-brand-warm" />
          <span className="text-ink-secondary">{t('facilities.schedule.booked', 'Zarezerwowane')}</span>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-2 flex-wrap">
        <button
          type="button"
          onClick={setAllAvailable}
          className="px-3 py-1.5 text-xs bg-brand-confirm/20 hover:bg-brand-confirm/30 text-brand-confirm-deep dark:text-brand-confirm rounded-lg transition-colors"
        >
          {t('facilities.schedule.setAllAvailable', 'Ustaw wszystkie dostępne')}
        </button>
        <button
          type="button"
          onClick={setAllUnavailable}
          className="px-3 py-1.5 text-xs bg-surface-card hover:bg-surface-card-hover text-ink-primary rounded-lg transition-colors"
        >
          {t('facilities.schedule.setAllUnavailable', 'Ustaw wszystkie niedostępne')}
        </button>
        {scheduleMode === 'general' && (
          <button
            type="button"
            onClick={copyToOtherDays}
            className={`px-3 py-1.5 text-xs rounded-lg transition-colors flex items-center gap-1.5 ${
              justCopied
                ? 'bg-brand-confirm/20 text-brand-confirm-deep dark:text-brand-confirm'
                : 'bg-brand-primary/15 text-brand-primary hover:bg-brand-primary/25 dark:bg-white/90 dark:text-brand-primary-deep dark:hover:bg-white'
            }`}
          >
            {justCopied ? (
              <>
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                {t('facilities.schedule.copied', 'Skopiowano!')}
              </>
            ) : (
              t('facilities.schedule.copyToOther', 'Kopiuj do innych dni')
            )}
          </button>
        )}
      </div>

      {/* Time Slots Grid */}
      {isLoadingSpecificDate && scheduleMode === 'specific' ? (
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center gap-2 text-ink-secondary">
            <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-primary" />
            <span>{t('facilities.schedule.loadingSlots', 'Ładowanie slotów...')}</span>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-12 gap-1.5">
          {getCurrentSlots().map(slot => (
            <button
              key={slot.id}
              type="button"
              onClick={() => !slot.isBooked && toggleTimeSlot(slot.id)}
              disabled={slot.isBooked}
              className={`p-2 rounded-lg border text-xs font-medium transition-colors ${getSlotStyle(slot)}`}
            >
              {slot.time}
            </button>
          ))}
        </div>
      )}

      {/* Info Box */}
      <div className="flex items-start gap-2 p-3 bg-brand-primary/10 border border-brand-primary/30 rounded-lg">
        <svg className="w-5 h-5 text-brand-primary dark:text-brand-primary-light flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-sm text-brand-primary dark:text-ink-on-dark">
          {scheduleMode === 'general' ? (
            selectedDay === 'weekdays'
              ? t('facilities.schedule.weekdaysInfo', 'Ustaw dostępność obiektu w dni robocze (poniedziałek-piątek). Kliknij na slot, aby zmienić jego dostępność.')
              : selectedDay === 'saturday'
              ? t('facilities.schedule.saturdayInfo', 'Ustaw dostępność obiektu w soboty. Kliknij na slot, aby zmienić jego dostępność.')
              : t('facilities.schedule.sundayInfo', 'Ustaw dostępność obiektu w niedziele. Kliknij na slot, aby zmienić jego dostępność.')
          ) : (
            t('facilities.schedule.specificDateInfo', 'Ustaw dostępność dla wybranej daty. Ta konfiguracja nadpisze harmonogram ogólny dla tego dnia.')
          )}
        </p>
      </div>

      <p className="text-xs text-ink-muted italic">
        {t('facilities.schedule.noScheduleWarning', 'Brak harmonogramu sprawia, że obiekt nie będzie wyświetlany.')}
      </p>
    </div>
  );
}
